import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Clock, Plus, Trash, PencilSimple, X, FloppyDisk, MapPin, User,
  ShareNetwork, Copy, Check, ArrowSquareOut,
} from '@phosphor-icons/react';

const COMMON_ICONS = ['🎉', '👋', '🎮', '🎵', '🍔', '🎂', '🪔', '💐', '📸', '🎁', '🍰', '🥂', '🪩', '👋', '🎤'];

export default function TimelineTab({ event }) {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.event_id}/timeline`);
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
  }, [api, event.event_id]);

  useEffect(() => { load(); }, [load]);

  // Tick once a minute for the "now" indicator
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this timeline item?')) return;
    await api.delete(`/events/${event.event_id}/timeline/${itemId}`);
    load();
  };

  const publicUrl = `${window.location.origin}/party/${event.slug}/timeline`;
  const copyShare = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Compute which item is "now" — assumes today is event day
  const isEventToday = (() => {
    if (!event.event_date) return false;
    const tod = new Date().toISOString().slice(0, 10);
    return event.event_date.slice(0, 10) === tod;
  })();
  const nowHHMM = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  const currentIdx = isEventToday
    ? items.findIndex((it, i) => {
        const nxt = items[i + 1];
        return it.time <= nowHHMM && (!nxt || nxt.time > nowHHMM);
      })
    : -1;

  return (
    <div className="space-y-3" data-testid="timeline-section">
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <h3 className="font-heading text-sm font-bold">Event-Day Timeline</h3>
          <p className="text-xs text-neutral-400 mt-1">
            {items.length === 0 ? 'Build a minute-by-minute schedule for the event day. Share it with vendors and family.' : `${items.length} item${items.length === 1 ? '' : 's'}.${isEventToday ? ' Today is the event day — live "Now" indicator active.' : ''}`}
          </p>
        </div>
        {items.length > 0 && (
          <>
            <button onClick={copyShare} data-testid="timeline-share-btn"
              className="px-3 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5 flex items-center gap-1.5">
              {copied ? <Check size={14} className="text-green-400" /> : <ShareNetwork size={14} />}
              {copied ? 'Copied!' : 'Share Link'}
            </button>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer"
              data-testid="timeline-preview-btn"
              className="px-3 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5 flex items-center gap-1.5">
              <ArrowSquareOut size={14} /> Preview
            </a>
          </>
        )}
        <button onClick={() => setShowAdd(true)} data-testid="timeline-add-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 hover:opacity-90">
          <Plus size={14} weight="bold" /> Add
        </button>
      </div>

      {showAdd && (
        <TimelineForm event={event} onCancel={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }} />
      )}

      {editing && (
        <TimelineForm event={event} initial={editing} isEdit
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {items.length === 0 ? (
        <div className="glass-card p-8 text-center" data-testid="timeline-empty">
          <Clock size={32} className="mx-auto text-neutral-500 mb-2" />
          <p className="text-sm text-neutral-400">No timeline items yet. Add what happens at each time of the event day.</p>
        </div>
      ) : (
        <div className="glass-card p-5" data-testid="timeline-list">
          <div className="relative pl-12">
            {/* Vertical line */}
            <div className="absolute left-[42px] top-0 bottom-0 w-px bg-white/10" />
            {items.map((it, idx) => (
              <TimelineRow key={it.item_id} item={it} idx={idx}
                isCurrent={idx === currentIdx}
                onEdit={() => setEditing(it)}
                onDelete={() => handleDelete(it.item_id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineRow({ item, idx, isCurrent, onEdit, onDelete }) {
  return (
    <div className={`relative mb-5 last:mb-0 ${isCurrent ? 'animate-pulse-slow' : ''}`} data-testid={`timeline-item-${item.item_id}`}>
      {/* Time bubble */}
      <div className={`absolute -left-[46px] top-1 flex items-center justify-center w-9 h-9 rounded-full text-[11px] font-bold ${
        isCurrent ? 'bg-[#FF6B35] text-white shadow-[0_0_16px_rgba(255,107,53,0.6)]' : 'bg-white/5 text-neutral-300 border border-white/10'
      }`}>
        {item.time || '—'}
      </div>
      {isCurrent && (
        <span className="absolute -left-[55px] top-2.5 text-[9px] font-bold text-[#FF6B35] uppercase tracking-wider rotate-[-90deg] origin-left">NOW</span>
      )}
      <div className={`rounded-2xl px-4 py-3 ${isCurrent ? 'bg-[#FF6B35]/10 border border-[#FF6B35]/40' : 'bg-white/[0.03] border border-white/5'}`}>
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {item.icon && <span className="text-lg">{item.icon}</span>}
              <span className="text-sm font-bold text-white">{item.title}</span>
            </div>
            {(item.location || item.lead_owner || item.description) && (
              <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px] text-neutral-400">
                {item.location && <span><MapPin size={10} className="inline mr-0.5" />{item.location}</span>}
                {item.lead_owner && <span><User size={10} className="inline mr-0.5" />{item.lead_owner}</span>}
              </div>
            )}
            {item.description && <p className="text-[11px] text-neutral-400 mt-1">{item.description}</p>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onEdit} data-testid={`timeline-edit-${item.item_id}`}
              className="p-1.5 rounded-lg border border-white/10 hover:bg-white/5 text-neutral-400 hover:text-white">
              <PencilSimple size={11} />
            </button>
            <button onClick={onDelete} data-testid={`timeline-delete-${item.item_id}`}
              className="p-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10">
              <Trash size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineForm({ event, initial, isEdit, onCancel, onSaved }) {
  const { api } = useAuth();
  const [form, setForm] = useState({
    time: initial?.time || (event.event_time || ''),
    title: initial?.title || '',
    description: initial?.description || '',
    location: initial?.location || event.venue || '',
    lead_owner: initial?.lead_owner || '',
    icon: initial?.icon || '🎉',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/events/${event.event_id}/timeline/${initial.item_id}`, form);
      } else {
        await api.post(`/events/${event.event_id}/timeline`, form);
      }
      onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div className="glass-card p-4 border border-[#FF6B35]/30 space-y-3" data-testid="timeline-form">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Time">
          <input value={form.time} onChange={(e) => upd('time', e.target.value)}
            data-testid="tl-time" placeholder="HH:MM, 4pm, 5:30 PM"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Title *">
          <input value={form.title} onChange={(e) => upd('title', e.target.value)}
            data-testid="tl-title" placeholder="e.g. Cake Cutting"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Location">
          <input value={form.location} onChange={(e) => upd('location', e.target.value)}
            data-testid="tl-location" placeholder="Main hall, Garden..."
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
        <Field label="Lead / Owner">
          <input value={form.lead_owner} onChange={(e) => upd('lead_owner', e.target.value)}
            data-testid="tl-lead" placeholder="e.g. DJ Sharma, Mom"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </Field>
      </div>
      <Field label="Description (optional)">
        <input value={form.description} onChange={(e) => upd('description', e.target.value)}
          data-testid="tl-description" placeholder="What happens here..."
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
      </Field>
      <Field label="Icon">
        <div className="flex flex-wrap gap-1">
          {COMMON_ICONS.map((emoji, i) => (
            <button key={`${emoji}-${i}`} type="button" onClick={() => upd('icon', emoji)}
              className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-all ${form.icon === emoji ? 'bg-[#FF6B35]/20 ring-2 ring-[#FF6B35]' : 'bg-white/5 hover:bg-white/10'}`}>
              {emoji}
            </button>
          ))}
        </div>
      </Field>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">Cancel</button>
        <button onClick={save} disabled={saving || !form.title.trim()} data-testid="tl-save-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">{label}</span>
      {children}
    </label>
  );
}
